import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Modal from '../../components/Modal';
import BopRowActions, { RowIcon } from '../../components/BopRowActions';
import BopExcelTable, { BopExcelColumn } from '../../components/BopExcelTable';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { Societa, Utente } from '../../types';
import { getRuoloLabel } from '../../utils/ruoliLabel';
import '../../styles/impostazioni.css';

type NuovaSocietaForm = { nome: string; dominio: string; attiva: boolean; coloreAccento: string };
const EMPTY: NuovaSocietaForm = { nome: '', dominio: '', attiva: true, coloreAccento: '#5B4FB5' };

export default function SocietaPage() {
  const [items,    setItems]    = useState<Societa[]>([]);
  const [utenti,   setUtenti]   = useState<Utente[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [modal,    setModal]    = useState(false);
  const [form,     setForm]     = useState<NuovaSocietaForm>({ ...EMPTY });
  const [saving,   setSaving]   = useState(false);

  const navigate = useNavigate();
  const { pronta } = useFiltroSocieta();

  // Mappa societaId → utenti collegati (calcolata dai dati già in memoria)
  const utentiBySocieta = useMemo(() => {
    const map: Record<number, Utente[]> = {};
    for (const u of utenti) {
      for (const s of (u.societa ?? [])) {
        if (!map[s.id]) map[s.id] = [];
        map[s.id].push(u);
      }
    }
    return map;
  }, [utenti]);

  const load = () =>
    Promise.all([
      apiFetch<{ societa: Societa[] }>('/api/societa'),
      apiFetch<{ utenti: Utente[] }>('/api/utenti'),
    ])
      .then(([dSoc, dUt]) => {
        setItems(dSoc.societa);
        setUtenti(dUt.utenti);
        setLoading(false);
      })
      .catch((e) => { setError(e.message); setLoading(false); });

  useEffect(() => { if (pronta) load(); }, [pronta]); // eslint-disable-line

  const openCreate = () => { setForm({ ...EMPTY }); setModal(true); };
  const closeModal = () => { setModal(false); setError(''); };

  const save = async () => {
    if (!form.nome || !form.dominio) { setError('Nome e dominio sono obbligatori'); return; }
    setSaving(true);
    try {
      // @api POST /api/societa {"nome":"string","dominio":"string","attiva":"boolean","coloreAccento":"string"} -> {"societa":{"id":"number"}}
      const r = await apiFetch<{ societa: Societa }>('/api/societa', { method: 'POST', body: JSON.stringify(form) });
      closeModal();
      if (r?.societa?.id) navigate(`/impostazioni/societa/${r.societa.id}`);
      else load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Eliminare questa società?')) return;
    try { await apiFetch(`/api/societa/${id}`, { method: 'DELETE' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const columns = useMemo<BopExcelColumn<Societa>[]>(() => [
    {
      key: 'nome',
      header: 'Nome',
      accessor: (s) => s.nome,
      filter: { type: 'text' },
      render: (s) => (
        <span className="impostazioni-societa-nome-cell">
          {/* Color dot ha valore dinamico (color da DB) — eccezione legittima a "no inline style" */}
          <span
            className="bop-societa-color-dot"
            style={{ backgroundColor: s.coloreAccento ?? '#5B4FB5' }}
          />
          <strong>{s.nome}</strong>
        </span>
      ),
    },
    {
      key: 'dominio',
      header: 'Dominio',
      accessor: (s) => s.dominio,
      filter: { type: 'text' },
      cellClassName: 'impostazioni-text-muted',
      render: (s) => s.dominio,
    },
    {
      key: 'utenti',
      header: 'Utenti',
      accessor: (s) => (utentiBySocieta[s.id] ?? []).length,
      filter: { type: 'number' },
      width: '130px',
      render: (s) => {
        const utentiSoc = utentiBySocieta[s.id] ?? [];
        if (utentiSoc.length === 0) {
          return <span className="impostazioni-text-empty">—</span>;
        }
        const tooltip = utentiSoc
          .map(u => `${u.nome} (${getRuoloLabel(u.ruolo)})`)
          .join('\n');
        return (
          <span title={tooltip}>
            {utentiSoc.length} utent{utentiSoc.length === 1 ? 'e' : 'i'}
          </span>
        );
      },
    },
    {
      key: 'attiva',
      header: 'Stato',
      accessor: (s) => s.attiva ? 'ATTIVA' : 'DISATTIVA',
      filter: {
        type: 'enum',
        options: [
          { value: 'ATTIVA',    label: 'Attiva' },
          { value: 'DISATTIVA', label: 'Disattiva' },
        ],
      },
      width: '110px',
      render: (s) => (
        <span className={`bop-badge ${s.attiva ? 'bop-badge-success' : 'bop-badge-danger'}`}>
          {s.attiva ? 'Attiva' : 'Disattiva'}
        </span>
      ),
    },
  ], [utentiBySocieta]);

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h1 className="bop-page-title">Società</h1>
          <p className="bop-text-muted">Gestione delle aziende registrate nel sistema</p>
        </div>
        <button className="bop-btn bop-btn-primary" onClick={openCreate}>+ Nuova società</button>
      </div>

      {error && !modal && <div className="bop-inline-error">{error}</div>}

      <BopExcelTable<Societa>
        data={items}
        loading={loading}
        rowKey={(s) => s.id}
        onRowClick={(s) => navigate(`/impostazioni/societa/${s.id}`)}
        initialSort={{ key: 'nome', dir: 'asc' }}
        emptyState={<div className="bop-empty-state">Nessuna società. Creane una.</div>}
        columns={columns}
        rowActions={(s) => (
          <BopRowActions actions={[
            { icon: <RowIcon.Trash />, label: 'Elimina', onClick: () => remove(s.id), variant: 'danger' },
          ]} />
        )}
      />

      {modal && (
        <Modal
          title="Nuova società"
          onClose={closeModal}
          footer={
            <>
              <button className="bop-btn bop-btn-secondary" onClick={closeModal}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Salvo…' : 'Salva'}
              </button>
            </>
          }
        >
          {error && <div className="bop-inline-error">{error}</div>}
          <div className="bop-form-group">
            <label>Nome <span className="req">*</span></label>
            <input className="bop-input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
          </div>
          <div className="bop-form-group">
            <label>Dominio email <span className="req">*</span></label>
            <input className="bop-input" placeholder="es. azienda.it" value={form.dominio} onChange={(e) => setForm({ ...form, dominio: e.target.value })} />
          </div>
          <div className="bop-form-group">
            <label>Colore società</label>
            <div className="bop-color-picker-row">
              <input
                type="color"
                className="bop-color-input"
                value={form.coloreAccento}
                onChange={(e) => setForm({ ...form, coloreAccento: e.target.value })}
              />
              <span className="bop-color-hex-label">{form.coloreAccento}</span>
            </div>
          </div>
          <div className="bop-form-group">
            <label className="form-check">
              <input type="checkbox" checked={form.attiva} onChange={(e) => setForm({ ...form, attiva: e.target.checked })} />
              Attiva
            </label>
          </div>
        </Modal>
      )}
    </Layout>
  );
}
